import { useEffect, useState, useCallback, useRef } from "react";
import * as global from '../data/global';
import { restoreSelectedSite, markSite3dPinned, saveSelectedSite } from '../data/global';
import * as Components from '../components/@index.js';
import { MuiMaterials } from '../@index.js';
import { useNavigate, useLocation } from "react-router-dom";
import { createApiEndpoint } from '../index.js';
import {
    WindowChrome,
    SiteSelectPhase,
    SITE_WINDOW_IDS,
} from './windowChrome/index.js';

const SITE3D_REFRESH_MS = 60 * 60 * 1000;

const mapFetchUserSitesToSiteInfos = (sites) =>
    (sites || []).map((_, siteIndex) => ({
        siteIndex,
        siteGuid: _.siteGuid,
        siteName: _.main?.siteName ?? "Site",
        bins: _.bins,
        isHms: _.isHms,
        currentTemperature: _.currentTemperature,
        currentHumidity: _.currentHumidity,
        currentPressure: _.currentPressue,
        lastScanDate: _.lastScan ? new Date(String(_.lastScan).split(".")[0]) : null,
        lastScan: _.lastScan
            ? new Date(_.lastScan).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "")
            : "",
        taskSlots: Array.isArray(_.taskSlots) ? _.taskSlots : [],
        siteWeatherStationReadingSettings: [],
        siteWeatherStationReadings: (global.gw.currentWeatherReadings || []).filter(
            (x) => x.siteId === _.siteGuid,
        ),
    }));

const ensureSiteInfosLoaded = async () => {
    if (global.gw.siteInfos?.length > 0) return true;
    try {
        const response = await createApiEndpoint().fetchUserSites("compressed");
        const sites = response?.data?.fetchUserAttribute?.sites;
        if (!sites?.length) return false;
        global.gw.siteInfos = mapFetchUserSitesToSiteInfos(sites);
        return true;
    } catch (error) {
        console.error("[Site3dWindow] Failed to load site list", error);
        return false;
    }
};

const waitForAuthToken = async () => {
    if (global.gw.token && global.gw.auth) return;
    let checkCount = 0;
    const maxChecks = 50;
    await new Promise((resolve) => {
        const checkToken = setInterval(() => {
            checkCount++;
            if ((global.gw.token && global.gw.auth) || checkCount >= maxChecks) {
                clearInterval(checkToken);
                resolve();
            }
        }, 100);
    });
};

const findSiteInfo = (siteGuid) =>
    (global.gw.siteInfos || []).find(
        (s) =>
            global.normalizeGuidForCompare(s.siteGuid) ===
            global.normalizeGuidForCompare(siteGuid),
    );

const Site3dWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [phase, setPhase] = useState("boot"); // boot | select | view
    const [pickerSiteGuid, setPickerSiteGuid] = useState("");
    const [sitesLoading, setSitesLoading] = useState(true);
    const [isLoading, setLoading] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);
    const [cableGradesLoaded, setCableGradesLoaded] = useState(false);
    const [siteViewKey, setSiteViewKey] = useState(0);
    const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
    const [saveViewModalApplyAllUsers, setSaveViewModalApplyAllUsers] = useState(true);
    const [saveViewError, setSaveViewError] = useState(null);
    const [headerActionsHost, setHeaderActionsHost] = useState(null);
    const switchInFlightRef = useRef(false);
    const bootHandledRef = useRef(false);

    const onOpenSaveModal = useCallback(() => {
        setSaveViewModalApplyAllUsers(true);
        setSaveViewError(null);
        setSaveViewModalOpen(true);
    }, []);

    const fetchGradesForSelectedSite = useCallback(() => {
        if (
            !global.gw.selectedSite?.isSiteInitialized
            || !global.gw.selectedSite?.siteGuid
            || global.gw.selectedSite?.isHms
        ) {
            return;
        }
        Promise.all([
            createApiEndpoint().fetchCableGrades("direct", { date: null }),
            createApiEndpoint().fetchBinGrades("direct", { date: null }),
            createApiEndpoint().fetchSiteGrade("direct", { date: null }, global.gw.selectedSite.siteGuid),
        ]).then(([cableGradesResponse, binGradesResponse, siteGradeResponse]) => {
            if (cableGradesResponse?.data?.getCableGrades) {
                try {
                    const arrayBuffer = global.base64StringToArrayBuffer(cableGradesResponse.data.getCableGrades);
                    global.decompress(arrayBuffer, "gzip")
                        .then((decompressed) => {
                            const responseData = JSON.parse(decompressed);
                            const gradesMap = new Map();
                            if (responseData && Array.isArray(responseData)) {
                                responseData.forEach((cg) => {
                                    const binId = typeof cg.BinId === "number" ? cg.BinId : parseInt(cg.BinId, 10);
                                    const cableId = typeof cg.CableId === "number" ? cg.CableId : parseInt(cg.CableId, 10);
                                    if (!isNaN(binId) && !isNaN(cableId)) {
                                        const dateRead = cg.DateRead ?? cg.dateRead ?? null;
                                        gradesMap.set(`${binId}_${cableId}`, {
                                            grade: cg.Grade,
                                            averageTimeToRead: cg.AverageTimeToRead,
                                            dateRead,
                                        });
                                    }
                                });
                            }
                            global.gw.selectedSite.cableGrades = gradesMap;
                        })
                        .catch((err) => console.error("Error parsing cable grades:", err));
                } catch (err) {
                    console.error("Error parsing cable grades:", err);
                }
            }
            if (binGradesResponse?.data?.getBinGrades) {
                try {
                    const arrayBuffer = global.base64StringToArrayBuffer(binGradesResponse.data.getBinGrades);
                    global.decompress(arrayBuffer, "gzip")
                        .then((decompressed) => {
                            const responseData = JSON.parse(decompressed);
                            const gradesMap = new Map();
                            if (responseData && Array.isArray(responseData)) {
                                responseData.forEach((bg) => {
                                    const binId = typeof bg.BinId === "number" ? bg.BinId : parseInt(bg.BinId, 10);
                                    if (!isNaN(binId)) {
                                        const dateRead = bg.DateRead ?? bg.dateRead ?? null;
                                        gradesMap.set(binId, {
                                            grade: bg.Grade,
                                            averageTimeToRead: bg.AverageTimeToRead,
                                            dateRead,
                                        });
                                    }
                                });
                            }
                            global.gw.selectedSite.binGrades = gradesMap;
                        })
                        .catch((err) => console.error("Error parsing bin grades:", err));
                } catch (err) {
                    console.error("Error parsing bin grades:", err);
                }
            }
            if (siteGradeResponse?.data?.getSiteGrade) {
                try {
                    const arrayBuffer = global.base64StringToArrayBuffer(siteGradeResponse.data.getSiteGrade);
                    global.decompress(arrayBuffer, "gzip")
                        .then((decompressed) => {
                            const responseData = JSON.parse(decompressed);
                            if (responseData && typeof responseData.Grade === "number") {
                                global.gw.selectedSite.siteGrade = responseData.Grade;
                            }
                        })
                        .catch((err) => console.error("Error parsing site grade:", err));
                } catch (err) {
                    console.error("Error parsing site grade:", err);
                }
            }
            setCableGradesLoaded(true);
        }).catch((err) => console.error("Error fetching grades for Site3d:", err));
    }, []);

    const runPostSiteInit = useCallback(() => {
        global.setSiteBinLevels();
        global.resetSite3dToSiteOverview?.();
        global.initializeHistory((loading) => {
            setIsHistoryLoading(loading);
        }, navigate);
        if (!global.gw.selectedSite.layoutCables) {
            global.gw.selectedSite.layoutCables = [];
            global.gw.selectedSite.isSiteInitialized = true;
        }
        fetchGradesForSelectedSite();
    }, [navigate, fetchGradesForSelectedSite]);

    const assignSelectedSiteFromInfo = useCallback((info) => {
        if (global.gw.selectedSite?.refreshId) {
            clearTimeout(global.gw.selectedSite.refreshId);
        }
        global.gw.selectedSite = new global.Site();
        global.gw.selectedSite.siteIndex = info.siteIndex;
        global.gw.selectedSite.siteGuid = info.siteGuid;
        global.gw.selectedSite.siteName = info.siteName;
        global.gw.selectedSite.lastScan = info.lastScan;
        global.gw.selectedSite.isSiteInitialized = false;
        saveSelectedSite();
        markSite3dPinned();
    }, []);

    const loadSite3d = useCallback(async (siteGuid, { remount = true } = {}) => {
        if (!siteGuid || switchInFlightRef.current) return false;
        const info = findSiteInfo(siteGuid);
        if (!info) return false;

        switchInFlightRef.current = true;
        setPhase("view");
        setLoading(true);
        setCableGradesLoaded(false);
        setIsHistoryLoading(true);

        if (global.gw.weatherStationAbortController) {
            global.gw.weatherStationAbortController.abort();
            global.gw.weatherStationAbortController = null;
        }
        if (global.gw.dashboardInterval) {
            clearInterval(global.gw.dashboardInterval);
            global.gw.dashboardInterval = null;
        }

        try {
            await waitForAuthToken();
            // Always drop disposed Three refs before (re)mount — title-nav return often
            // reuses an already-initialized selectedSite and would otherwise pan/fit a dead scene.
            global.prepareSite3dSceneMount?.();

            const alreadyReady =
                global.gw.selectedSite?.siteGuid === info.siteGuid
                && global.gw.selectedSite?.isSiteInitialized
                && !global.isSiteLayoutEditorReturn?.();

            if (!alreadyReady) {
                if (global.gw.selectedSite?.siteGuid !== info.siteGuid || !global.gw.selectedSite?.isSiteInitialized) {
                    assignSelectedSiteFromInfo(info);
                    global.prepareSite3dSceneMount?.();
                }
                if (!global.gw.selectedSite.isSiteInitialized) {
                    await global.initializeCustomerSite(navigate);
                } else if (global.isSiteLayoutEditorReturn?.()) {
                    await global.refreshSite3dFromLayoutEditor();
                }
            } else {
                markSite3dPinned();
            }

            runPostSiteInit();
            setSiteViewKey((k) => k + 1);
            return true;
        } catch (error) {
            console.error("[Site3dWindow] Site load failed", error);
            if (!global.gw.selectedSite.layoutCables) {
                global.gw.selectedSite.layoutCables = [];
                global.gw.selectedSite.isSiteInitialized = true;
            }
            global.gw.selectedSite.initializationError = true;
            return false;
        } finally {
            setLoading(false);
            switchInFlightRef.current = false;
        }
    }, [assignSelectedSiteFromInfo, navigate, runPostSiteInit]);

    const onSiteChange = useCallback(async (siteGuid) => {
        if (!siteGuid || siteGuid === global.gw.selectedSite?.siteGuid) return;
        setPickerSiteGuid(siteGuid);
        await loadSite3d(siteGuid, { remount: true });
    }, [loadSite3d]);

    const handleContinueFromPicker = useCallback(async (guid) => {
        if (!guid) return;
        setPickerSiteGuid(guid);
        await loadSite3d(guid, { remount: true });
    }, [loadSite3d]);

    // Hourly refresh only while viewing 3D.
    useEffect(() => {
        if (phase !== "view") return undefined;
        markSite3dPinned();
        const refreshTimerId = setInterval(() => {
            window.location.reload();
        }, SITE3D_REFRESH_MS);
        return () => clearInterval(refreshTimerId);
    }, [phase]);

    // Boot: load site list, then either show picker or auto-load.
    useEffect(() => {
        if (bootHandledRef.current) return;
        bootHandledRef.current = true;

        (async () => {
            setSitesLoading(true);
            const sitesOk = await ensureSiteInfosLoaded();
            setSitesLoading(false);

            if (!sitesOk && !(global.gw.siteInfos?.length > 0)) {
                navigate("/sites", { replace: true });
                return;
            }

            const openPicker = location.state?.openSitePicker === true;
            const autoLoadGuid =
                location.state?.autoLoad && location.state?.siteGuid
                    ? location.state.siteGuid
                    : null;

            // Clear one-shot navigation state so refresh/back doesn't re-trigger oddly.
            if (location.state?.openSitePicker || location.state?.autoLoad) {
                navigate(location.pathname, { replace: true, state: {} });
            }

            if (openPicker) {
                setPhase("select");
                setLoading(false);
                return;
            }

            let guidToLoad = autoLoadGuid || global.gw.selectedSite?.siteGuid || null;

            if (!guidToLoad) {
                const savedSite = restoreSelectedSite();
                if (savedSite?.siteGuid) {
                    guidToLoad = savedSite.siteGuid;
                    global.gw.selectedSite.siteIndex = savedSite.siteIndex;
                    global.gw.selectedSite.siteGuid = savedSite.siteGuid;
                    global.gw.selectedSite.siteName = savedSite.siteName;
                    if (savedSite.lastScan) {
                        global.gw.selectedSite.lastScan = savedSite.lastScan;
                    }
                }
            }

            // Never pick a random/first site — show the modal selector instead.
            if (!guidToLoad || !findSiteInfo(guidToLoad)) {
                setPhase("select");
                setLoading(false);
                return;
            }

            setPickerSiteGuid(guidToLoad);
            await loadSite3d(guidToLoad, { remount: false });
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once boot
    }, []);

    // Title-nav / re-entry can land on /site3d with a new site while mounted.
    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (siteGuid === global.gw.selectedSite?.siteGuid && phase === "view") {
            navigate(location.pathname, { replace: true, state: {} });
            return;
        }
        setPickerSiteGuid(siteGuid);
        loadSite3d(siteGuid, { remount: true }).then(() => {
            navigate(location.pathname, { replace: true, state: {} });
        });
    }, [location.state, location.pathname, loadSite3d, navigate, phase]);

    useEffect(() => {
        if (location.state?.openSitePicker !== true) return;
        setPhase("select");
        setPickerSiteGuid("");
        setLoading(false);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    const getThemeColor = (lightColor, darkColor) => {
        if (global.gw.theme === "light") {
            return lightColor;
        }
        return darkColor;
    };

    const handleClose = useCallback(() => {
        if (global.gw.selectedSite?.refreshId) {
            clearTimeout(global.gw.selectedSite.refreshId);
        }
        global.clearSite3dPinned?.();
        if (global.isAdminUser(global.gw.email) && global.isSiteLayoutEditorReturn?.()) {
            if (global.navigateBackToSiteLayout(navigate)) {
                return;
            }
        } else {
            global.clearSiteLayoutReturn?.();
        }
        navigate("/sites", { replace: true });
    }, [navigate]);

    const viewSiteGuid = pickerSiteGuid || global.gw.selectedSite?.siteGuid || null;

    const loadingOverlay = isLoading || sitesLoading ? (
        <MuiMaterials.Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: getThemeColor("rgba(255,255,255,0.65)", "rgba(0,0,0,0.55)"),
            }}
        >
            <MuiMaterials.RotatingLines
                strokeColor={getThemeColor("#333333", "#f4cc8e")}
                strokeWidth="3"
                animationDuration="0.72"
                width="48"
                visible
            />
        </MuiMaterials.Box>
    ) : null;

    if (phase === "boot") {
        return (
            <MuiMaterials.Box
                sx={{
                    fontSize: "50px",
                    color: getThemeColor("rgb(51, 51, 51)", "rgb(244, 204, 142)"),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "fixed",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                    width: "100%",
                    height: "100%",
                    background: getThemeColor("rgba(255, 255, 255, 0.9)", "rgba(0, 0, 0, 0.8)"),
                    zIndex: 9999,
                }}
            >
                <MuiMaterials.RotatingLines
                    strokeColor={getThemeColor("#333333", "#f4cc8e")}
                    strokeWidth="3"
                    animationDuration="0.72"
                    width="48"
                    visible={true}
                />
            </MuiMaterials.Box>
        );
    }

    if (phase === "select") {
        return (
            <WindowChrome
                title="Site 3D"
                toolTitle="Site 3D"
                siteWindowId={SITE_WINDOW_IDS.sites3d}
                siteGuid={pickerSiteGuid || global.gw.selectedSite?.siteGuid || null}
                onClose={handleClose}
                loadingOverlay={loadingOverlay}
            >
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={handleContinueFromPicker}
                    onCancel={handleClose}
                    loading={sitesLoading || isLoading}
                    filterSite={(s) => !s.isHms}
                />
            </WindowChrome>
        );
    }

    const renderSite3d = () => (
        <WindowChrome
            className="site3dWindow hide-scrollbar"
            title="Site 3D"
            toolTitle="Site 3D"
            siteWindowId={SITE_WINDOW_IDS.sites3d}
            siteGuid={viewSiteGuid}
            showHeaderSiteSelect
            onHeaderSiteChange={onSiteChange}
            headerFilterSite={(s) => !s.isHms}
            headerSiteSelectDisabled={isLoading}
            onClose={handleClose}
            loadingOverlay={isLoading ? loadingOverlay : null}
            actions={
                !isLoading ? (
                    <MuiMaterials.Box
                        ref={setHeaderActionsHost}
                        sx={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: 1,
                        }}
                    />
                ) : null
            }
        >
            <MuiMaterials.Box
                name="site3d"
                className="dragResize"
                sx={{
                    overflow: "hidden",
                    textAlign: "center",
                    background: getThemeColor("#ffffff", "#2d2d2d"),
                    display: "flex",
                    justifyContent: "center",
                    alignContent: "center",
                    alignItems: "stretch",
                    width: "100%",
                    height: "100%",
                    minHeight: 0,
                    flex: 1,
                    position: "relative",
                    color: getThemeColor("#333333", "#f4cc8e"),
                }}
            >
                {!isLoading ? (
                    <div style={{ display: "contents" }}>
                        <Components.Site3d
                            key={`sitecard_site3d_${siteViewKey}_${global.gw.selectedSite?.siteGuid || "none"}`}
                            isHistoryLoading={isHistoryLoading}
                            onOpenSaveModal={onOpenSaveModal}
                            chromeEmbedded
                            headerActionsHost={headerActionsHost}
                        />
                    </div>
                ) : null}
            </MuiMaterials.Box>
        </WindowChrome>
    );
    return (
        <>
            {renderSite3d()}
            <MuiMaterials.Modal
                open={saveViewModalOpen}
                onClose={() => {
                    setSaveViewModalOpen(false);
                    setSaveViewModalApplyAllUsers(false);
                    setSaveViewError(null);
                }}
                disableScrollLock
                disableEnforceFocus
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                <MuiMaterials.Box
                    sx={{
                        backgroundColor: getThemeColor("#fff", "#2d2d2d"),
                        p: 2,
                        borderRadius: 1,
                        minWidth: 320,
                        maxWidth: 360,
                    }}
                >
                    <MuiMaterials.Typography variant="h6" sx={{ mb: 2, color: getThemeColor("#333", "#f4cc8e") }}>
                        Save Site View
                    </MuiMaterials.Typography>
                    <MuiMaterials.FormGroup>
                        {global.isAdminUser(global.gw.email) && (
                            <MuiMaterials.FormControlLabel
                                control={
                                    <MuiMaterials.Checkbox
                                        checked={saveViewModalApplyAllUsers}
                                        onChange={(e) => setSaveViewModalApplyAllUsers(e.target.checked)}
                                        sx={{ color: getThemeColor("#333", "#f4cc8e") }}
                                    />
                                }
                                label="Apply to all users (administrator)"
                                sx={{ color: getThemeColor("#333", "#ccc") }}
                            />
                        )}
                    </MuiMaterials.FormGroup>
                    {saveViewError && (
                        <MuiMaterials.Typography sx={{ color: "#d32f2f", fontSize: "0.875rem", mt: 1 }}>
                            {saveViewError}
                        </MuiMaterials.Typography>
                    )}
                    <MuiMaterials.Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <MuiMaterials.Button
                            onClick={() => setSaveViewModalOpen(false)}
                            sx={{ color: getThemeColor("#333", "#f4cc8e") }}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            variant="contained"
                            onClick={async () => {
                                setSaveViewError(null);
                                global.resetSite3dToSiteOverview?.();
                                const payload = global.buildSiteViewPayload();
                                if (!payload) {
                                    setSaveViewError(
                                        "Unable to capture view. Ensure you are in site overview (not a bin view).",
                                    );
                                    return;
                                }
                                if (!global.gw.selectedSite?.siteGuid) {
                                    setSaveViewError("No site selected.");
                                    return;
                                }
                                let ok;
                                try {
                                    ok = saveViewModalApplyAllUsers
                                        ? await createApiEndpoint().saveUserCustomerSiteSavedViewForAllUsers(
                                              "direct",
                                              null,
                                              "Site",
                                              0,
                                              payload,
                                          )
                                        : await createApiEndpoint().saveUserCustomerSiteSavedView(
                                              "direct",
                                              null,
                                              "Site",
                                              0,
                                              payload,
                                          );
                                } catch (err) {
                                    console.error("Save site view error:", err);
                                    ok = false;
                                }
                                if (ok) {
                                    setSaveViewModalOpen(false);
                                    setSaveViewModalApplyAllUsers(false);
                                } else {
                                    setSaveViewError(
                                        saveViewModalApplyAllUsers
                                            ? "Save for all users failed. Administrator permission required."
                                            : "Save failed. Please try again.",
                                    );
                                }
                            }}
                            sx={{
                                backgroundColor: getThemeColor("#333", "#f4cc8e"),
                                color: getThemeColor("#fff", "#333"),
                            }}
                        >
                            Save
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        </>
    );
};
export default Site3dWindow;
